import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
	GroupedSearchItemDto,
	SearchClient,
	SearchItemDto,
} from 'app/core/services/api.service';
import {
	Observable,
	Subject,
	debounceTime,
	takeUntil,
	map,
	of,
	filter,
	distinctUntilChanged,
} from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { QuickViewClaimComponent } from './quick-view-claim/quick-view-claim.component';
import { AsyncPipe } from '@angular/common';
import { QuickViewCompanyComponent } from './quick-view-company/quick-view-company.component';
import { QuickViewContactComponent } from './quick-view-contact/quick-view-contact.component';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { CamelCaseStringPipe } from '../../shared/pipes/camel-case-string.pipe';
import { QuickViewLeadComponent } from './quick-view-lead/quick-view-lead.component';
import { QuickViewUserComponent } from './quick-view-user/quick-view-user.component';

@Component({
    selector: 'cm-search-command-palette',
    templateUrl: './search-command-palette.component.html',
    imports: [
        AsyncPipe,
        ReactiveFormsModule,
        MatIconModule,
        QuickViewClaimComponent,
        QuickViewCompanyComponent,
        QuickViewContactComponent,
        QuickViewLeadComponent,
        QuickViewUserComponent,
        CamelCaseStringPipe,
    ]
})
export class SearchCommandPaletteDialog implements OnInit, OnDestroy {
	searchControl: FormControl = new FormControl();
	searchResult$: Observable<GroupedSearchItemDto[]>;
	searchResultLength: number = 0;
	searchItem: SearchItemDto;

	isSmallScreen: boolean = false;

	private _unsubscribeAll: Subject<any> = new Subject<any>();

	constructor(
		@Inject(MAT_DIALOG_DATA) private config: any,
		private _dialogRef: MatDialogRef<SearchCommandPaletteDialog>,
		private _searchClient: SearchClient,
		private _router: Router,
		private _fuseMediaWatcherService: FuseMediaWatcherService
	) {}

	// -----------------------------------------------------------------------------------------------------
	// @ Lifecycle hooks
	// -----------------------------------------------------------------------------------------------------

	/**
	 * On init
	 */
	ngOnInit(): void {
		this._dialogRef.addPanelClass('search-command-palette-dialog');

		this._dialogRef
			.keydownEvents()
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe({
				next: (event) => {
					if (event.key === 'Escape') {
						this.onCancel();
					}
				},
			});

		this._dialogRef
			.backdropClick()
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe({
				next: () => this.onCancel(),
			});

		this.searchControl.valueChanges
			.pipe(
				filter((query) => !query || String(query).length >= 3),
				debounceTime(300),
				distinctUntilChanged(),
				takeUntil(this._unsubscribeAll)
			)
			.subscribe({
				next: (value) => this.onSearch(value),
			});

		// Subscribe to media changes
		this._fuseMediaWatcherService.onMediaChange$
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe(({ matchingAliases }) => {
				this.isSmallScreen = !matchingAliases.includes('md');

				if (matchingAliases.includes('md')) this.onCancel();
			});
	}

	ngOnDestroy(): void {
		this._unsubscribeAll.next(null);
		this._unsubscribeAll.complete();
	}

	/** Cancel dialog */
	onCancel() {
		let output: any;

		// Return output data by accessing material dialog afterClose event
		this._dialogRef.close(output);
	}

	onMouseEnter(searchItem: SearchItemDto) {
		this.searchItem = searchItem;
	}

	onSearch(searchPhrase: string) {
		if (!searchPhrase) {
			this.searchResultLength = 0;
			this.searchResult$ = of([]);

			return;
		}

		this.searchResult$ = this._searchClient.search(searchPhrase).pipe(
			map((result) => {
				this.searchResultLength = result.length;
				return result;
			})
		);
	}

	goToPage(searchItem?: SearchItemDto) {
		let { link } = searchItem || this.searchItem;

		this._router.navigateByUrl(link);
		this._dialogRef.close();
	}

	closeSearch() {
		this._dialogRef.close();
	}
}
